import React, { useState, useMemo } from 'react';
import {
  Button,
  Card,
  Typography,
  Alert,
  Space,
  Form,
  Checkbox,
  Modal,
  Steps,
  Spin,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { usePpiAnalysis } from './hooks/usePpiAnalysis';
import { useUser } from '../../context/UserContext';
import {
  VehicleOverviewPage,
  MechanicalConditionPage,
  ValueEligibilityPage,
} from './components/ReportPages';
import { NextSteps } from './components';
import { AnalysisStep, AnalysisStepsState, SimilarCar } from '../../types';

const { Title, Text } = Typography;
const { Step } = Steps;

const reportPages = [
  {
    key: 'email_entry',
    name: 'Create Report',
    steps: [],
  },
  {
    key: 'vehicle_overview',
    name: 'Vehicle Overview',
    steps: ['flags_and_highlights'],
  },
  {
    key: 'mechanical_condition',
    name: 'Condition Report',
    steps: ['common_issues', 'maintenance', 'image_analysis', 'suggested_repairs'],
  },
  {
    key: 'value_eligibility',
    name: 'Value & Eligibility',
    steps: ['cpo_eligibility', 'market_insights'],
  },
  {
    key: 'next_steps',
    name: 'Next Steps',
    steps: [],
  },
];

const getPageStatus = (page: any, analysisSteps: AnalysisStepsState, analysisStarted: boolean) => {
  if (page.steps.length === 0) {
    return 'completed'; 
  }

  const pageSteps = page.steps.map((key: string) => analysisSteps[key]);

  if (pageSteps.some((step: AnalysisStep) => step?.status === 'error')) {
    return 'error';
  }
  if (pageSteps.every((step: AnalysisStep) => step?.status === 'completed' || step?.status === 'skipped')) {
    return 'completed';
  }
  if (pageSteps.some((step: AnalysisStep) => step?.status === 'in_progress')) {
    return 'in_progress';
  }
  if (analysisStarted && pageSteps.some((step: AnalysisStep) => step?.status === 'pending')) {
      return 'in_progress';
  }

  return 'pending';
};

interface CreatePpiReportProps {
  docId: string;
  onComplete?: () => void;
  onBack?: () => void;
}

const CreatePpiReport: React.FC<CreatePpiReportProps> = ({ docId, onComplete, onBack }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sendEmail, setSendEmail] = useState(false);
  const { email } = useUser();

  const {
    loading,
    error,
    analysisStarted,
    analysisSteps,
    isComplete,
    startAnalysis,
    sentEmailAddress,
    emailError,
    resetState,
    similarCars,
  } = usePpiAnalysis();

  const pageStatuses = useMemo(() => reportPages.map(page => getPageStatus(page, analysisSteps, analysisStarted)), [analysisSteps, analysisStarted]);

  const handleNext = () => {
    if (currentPage < reportPages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleStepClick = (index: number) => {
    const page = reportPages[index];
    const status = pageStatuses[index];

    if (page.key === 'email_entry' || (status !== 'pending' && status !== 'in_progress') || (page.key === 'next_steps' && isComplete)) {
      setCurrentPage(index);
    }
  };

  const handleStartAnalysis = () => {
    startAnalysis(sendEmail);
    handleNext();
  };

  const handleRerunAnalysis = () => {
    Modal.confirm({
      title: 'Rerun Analysis?',
      icon: <ExclamationCircleOutlined />,
      content: 'This will rerun the analysis with the latest information. Only do this if you have added new details like images or a vehicle history report.',
      okText: 'Confirm Rerun',
      onOk: () => {
        resetState();
        handleStartAnalysis();
      },
    });
  };

  const renderCurrentPage = () => {
    const page = reportPages[currentPage];
    const status = pageStatuses[currentPage];

    if (analysisStarted && status === 'in_progress' && page.key !== 'email_entry') {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <LoadingOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>Analysis in progress...</Title>
          <Text type="secondary">Compiling {page.name}</Text>
        </div>
      );
    }

    switch (page.key) {
      case 'email_entry':
        return (
          <Card>
            <Title level={4}>Your Comprehensive Report is Ready to Generate</Title>
            <Text type="secondary">
              Our AI will now analyze the vehicle details, images, and history report to generate a your complete Pacer Scout Analysis.
            </Text>
            <Text type="secondary" style={{ marginTop: 16, display: 'block' }}>
              This Includes:
            </Text>
            <Space direction="vertical" style={{ marginBottom: 16, marginLeft: 16 }}>
              <Text>Highlights & Red Flags</Text>
              <Text>Common issues for this model</Text>
              <Text>Maintenance history & CPO eligibility</Text>
              <Text>Image & damage analysis</Text>
              <Text>Market insights & a buying guide with next steps</Text>
            </Space>
            <Form layout="vertical" style={{ marginTop: 24 }}>
              {email && (
                <Form.Item>
                  <Checkbox
                    checked={sendEmail}
                    onChange={(e: CheckboxChangeEvent) => setSendEmail(e.target.checked)}
                    disabled={loading}
                  >
                    Send a copy of the report to {email}
                  </Checkbox>
                </Form.Item>
              )}
            </Form>
          </Card>
        );
      case 'vehicle_overview':
        return <VehicleOverviewPage results={analysisSteps} />;
      case 'mechanical_condition':
        return <MechanicalConditionPage results={analysisSteps} />;
      case 'value_eligibility':
        return <ValueEligibilityPage results={analysisSteps} similarCars={similarCars} />;
      case 'next_steps':
        return (
          <NextSteps
            docId={docId}
            onBack={handleBack}
            email={sentEmailAddress}
            emailError={emailError}
            sendEmail={sendEmail}
            onComplete={handleNext}
          />
        );
      default:
        return null;
    }
  };

  const renderRightButtons = () => {
    const currentStepKey = reportPages[currentPage].key;
    const currentStatus = pageStatuses[currentPage];

    if (currentStepKey === 'email_entry') {
      if (analysisStarted && !isComplete) {
        return (
          <Button type="primary" onClick={handleNext} icon={<RightOutlined />}>
            Continue to Analysis
          </Button>
        );
      }
      if (isComplete) {
        return (
          <Space>
            <Button onClick={handleRerunAnalysis} danger>
              Rerun Analysis
            </Button>
            <Button type="primary" onClick={handleNext} icon={<RightOutlined />}>
              View Previous Report
            </Button>
          </Space>
        );
      }
      return (
        <Button type="primary" onClick={handleStartAnalysis} loading={loading}>
          Start Analysis
        </Button>
      );
    }

    if (currentStepKey === 'value_eligibility') {
      return (
        <Button
          type="primary"
          onClick={handleNext}
          icon={<RightOutlined />}
          disabled={currentStatus !== 'completed'}
        >
          Finish & View Report
        </Button>
      );
    }

    if (currentStepKey !== 'next_steps' && currentStepKey !== 'email_entry') {
      return (
        <Button
          type="primary"
          onClick={handleNext}
          icon={<RightOutlined />}
          disabled={currentStatus !== 'completed'}
        >
          Continue
        </Button>
      );
    }

    return null;
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '-270px', top: 0, width: '240px' }}>
        <Card>
          <Steps direction="vertical" current={currentPage} size="small">
            {reportPages.map((page, index) => {
              const status = pageStatuses[index];
              let icon;
              switch (status) {
                case 'in_progress':
                  icon = <Spin size="small" />;
                  break;
                case 'completed':
                  icon = <CheckCircleOutlined />;
                  break;
                case 'error':
                  icon = <CloseCircleOutlined />;
                  break;
                default:
                  if (page.key === 'next_steps' && isComplete) {
                    icon = <QuestionCircleOutlined />;
                  } else {
                    icon = undefined;
                  }
              }

              const isClickable = status === 'completed' || status === 'error' || (page.key === 'next_steps' && isComplete) || page.key === 'email_entry';

              return (
                <Step
                  key={page.key}
                  title={page.name}
                  icon={icon}
                  disabled={!isClickable}
                  onClick={() => handleStepClick(index)}
                  style={{ cursor: !isClickable ? 'not-allowed' : 'pointer' }}
                />
              );
            })}
          </Steps>
        </Card>
      </div>
      <div>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
          {reportPages[currentPage].name}
        </Title>
        <Card bodyStyle={{ padding: 0 }}>{renderCurrentPage()}</Card>
        {analysisStarted && !isComplete && (
          <Alert
            message="IMPORTANT: Please keep this page open while we finish your inspection. Closing or refreshing may interrupt your report."
            type="warning"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
        <Space
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginTop: 24,
          }}
        >
          <Button
            onClick={currentPage === 0 ? onBack : handleBack}
            icon={<LeftOutlined />}
            disabled={currentPage === 0 && (!onBack || (analysisStarted && !isComplete))}
          >
            Back
          </Button>
          {renderRightButtons()}
        </Space>
      </div>
    </div>
  );
};

export default CreatePpiReport;